import { z } from 'zod';
import { IdSchema, MetadataOnlyIntentSchema, PromptAnswerSchema } from './horde-event-schema-helpers';
import {
    AppliedModifierSchema,
    BattlefieldMarkerSchema,
    EffectInstanceSchema,
    HordeInstructionSchema,
    HordeSessionUnitSchema,
    MaterializedRandomResultSchema,
    ObjectiveStateSchema,
    RecordedTableFactSchema,
    RulesInterpretationConfigSchema,
    SpawnCompositionNodeSchema,
    SpawnZoneStateSchema,
} from './horde-session';

export const HordeIntentPayloadSchemasA = {
    ABANDON_GAME: MetadataOnlyIntentSchema.extend({
        reason: z.string().min(1),
    }),
    ADVANCE_WORKFLOW: MetadataOnlyIntentSchema,
    ANSWER_PROMPT: PromptAnswerSchema,
    APPLY_SECONDARY_RESULT: MetadataOnlyIntentSchema.extend({
        applicationId: IdSchema,
        secondaryCardId: IdSchema,
    }),
    APPLY_SPAWN_MODIFIERS: MetadataOnlyIntentSchema.extend({
        bracketId: IdSchema,
        modifiedTotal: z.number().int(),
        modifiers: z.array(AppliedModifierSchema),
    }),
    AWARD_CP: MetadataOnlyIntentSchema.extend({
        amount: z.number().int().positive(),
        playerId: IdSchema,
        sourceId: IdSchema,
    }),
    AWARD_OBJECTIVE_SP: MetadataOnlyIntentSchema.extend({
        controlFactIds: z.array(IdSchema),
        controlledObjectiveCount: z.number().int().nonnegative(),
    }),
    AWARD_SP: MetadataOnlyIntentSchema.extend({
        amount: z.number().int().positive(),
        playerIds: z.array(IdSchema).min(1),
        responsiblePlayerId: IdSchema.nullable(),
        soloDoublingApplied: z.boolean(),
        sourceId: IdSchema,
    }),
    BEGIN_HORDE_UNIT_ACTIVATION: MetadataOnlyIntentSchema.extend({
        unitId: IdSchema,
    }),
    CANCEL_MISERY_WITH_SAVED_REWARD: MetadataOnlyIntentSchema.extend({
        miseryRevealId: IdSchema,
        playerId: IdSchema,
        savedRewardCardId: IdSchema,
    }),
    CHOOSE_SECRET_OBJECTIVE: MetadataOnlyIntentSchema.extend({
        cardId: IdSchema,
        playerId: IdSchema,
        vaultRevision: z.number().int().nonnegative(),
    }),
    COMPLETE_EFFECT: MetadataOnlyIntentSchema.extend({
        effectId: IdSchema,
        resolutionId: IdSchema,
    }),
    COMPLETE_HORDE_UNIT_ACTIVATION: MetadataOnlyIntentSchema.extend({
        phase: z.enum(['command', 'movement', 'shooting', 'charge', 'fight']),
        unitId: IdSchema,
    }),
    COMPLETE_MISSION_ACTION: MetadataOnlyIntentSchema.extend({
        actionId: IdSchema,
        result: z
            .object({
                completed: z.boolean().optional(),
                reason: z.string().min(1).max(512).optional(),
            })
            .strict(),
    }),
    COMPLETE_PLAYER_TURN: MetadataOnlyIntentSchema,
    COMPLETE_SECRET_OBJECTIVE: MetadataOnlyIntentSchema.extend({
        playerId: IdSchema,
        reason: z.string().min(1),
        vaultRevision: z.number().int().nonnegative(),
    }),
    COMPLETE_SPAWN_WORKFLOW: MetadataOnlyIntentSchema,
    COMPLETE_SPAWN_ZONE: MetadataOnlyIntentSchema.extend({
        zoneId: IdSchema,
    }),
    CONFIRM_ARMY_CONSTRUCTION: MetadataOnlyIntentSchema.extend({
        playerListsConfirmed: z.boolean(),
    }),
    CONFIRM_CHARGE_RESULT: MetadataOnlyIntentSchema.extend({
        succeeded: z.boolean(),
        unitId: IdSchema,
    }),
    CONFIRM_CHARGE_TARGET: MetadataOnlyIntentSchema.extend({
        targetId: IdSchema,
        unitId: IdSchema,
    }),
    CONFIRM_CLEAR_SPAWN_ZONE: MetadataOnlyIntentSchema.extend({
        clearedUnits: z.array(
            z.object({
                bracketMinimum: z.number().int(),
                factionId: IdSchema,
                hordeUnitId: IdSchema.optional(),
                playerUnitRef: IdSchema.optional(),
            })
        ),
        zoneId: IdSchema,
    }),
    CONFIRM_CONSOLIDATION: MetadataOnlyIntentSchema.extend({
        unitId: IdSchema,
    }),
    CONFIRM_DELEGATED_PHASE_COMPLETE: MetadataOnlyIntentSchema.extend({
        phase: z.enum(['command', 'movement', 'shooting', 'charge', 'fight']),
        side: z.enum(['horde', 'players']),
    }),
    CONFIRM_DISEMBARK: MetadataOnlyIntentSchema.extend({
        disembarked: z.boolean(),
        unitId: IdSchema,
    }),
    CONFIRM_FIGHT_RESOLVED: MetadataOnlyIntentSchema.extend({
        targetIds: z.array(IdSchema),
        unitId: IdSchema,
    }),
    CONFIRM_MOVEMENT_ACTION: MetadataOnlyIntentSchema.extend({
        instruction: HordeInstructionSchema,
    }),
    CONFIRM_PILE_IN: MetadataOnlyIntentSchema.extend({
        targetIds: z.array(IdSchema),
        unitId: IdSchema,
    }),
    CONFIRM_PLAYER_DEPLOYMENT: MetadataOnlyIntentSchema,
    CONFIRM_SHOOTING_RESOLVED: MetadataOnlyIntentSchema.extend({
        targetIds: z.array(IdSchema),
        unitId: IdSchema,
        weaponGroupId: IdSchema.nullable(),
    }),
    CONFIRM_SPAWN_COMPOSITION: MetadataOnlyIntentSchema.extend({
        composition: z.array(SpawnCompositionNodeSchema),
    }),
    CONFIRM_START_OF_GAME_ABILITIES: MetadataOnlyIntentSchema,
    CONFIRM_TABLE_LAYOUT: MetadataOnlyIntentSchema.extend({
        centralObjectiveConfirmed: z.boolean(),
        eventCompanionLayoutConfirmed: z.boolean(),
        omittedObjectivesReplaced: z.boolean(),
        terrainClearOfDeploymentZones: z.boolean(),
    }),
    CONFIRM_UNIT_PLACEMENT: MetadataOnlyIntentSchema.extend({
        engagementRange: z.boolean(),
        overflowedIntoNoMansLand: z.boolean(),
        unit: HordeSessionUnitSchema,
        zoneId: IdSchema,
    }),
    CORRECT_REPORTED_FACT: MetadataOnlyIntentSchema.extend({
        reason: z.string().min(1),
        replacementFact: RecordedTableFactSchema,
        targetEventId: IdSchema,
    }),
    CREATE_BATTLEFIELD_MARKER: MetadataOnlyIntentSchema.extend({
        marker: BattlefieldMarkerSchema,
    }),
    CREATE_EFFECT: MetadataOnlyIntentSchema.extend({
        effect: EffectInstanceSchema,
    }),
    CREATE_GAME: MetadataOnlyIntentSchema.extend({
        name: z.string().min(1),
        rulesInterpretations: RulesInterpretationConfigSchema,
    }),
    CREATE_OBJECTIVE: MetadataOnlyIntentSchema.extend({
        objective: ObjectiveStateSchema,
    }),
    CREATE_SPAWN_ZONES: MetadataOnlyIntentSchema.extend({
        zones: z.array(SpawnZoneStateSchema),
    }),
    DEAL_SECRET_OFFERS: MetadataOnlyIntentSchema.extend({
        offersByPlayer: z.record(IdSchema, z.array(IdSchema).min(1).max(2)),
        randomResult: MaterializedRandomResultSchema,
        vaultRevision: z.number().int().nonnegative(),
    }),
    DEFER_HORDE_UNIT_TO_RESERVES: MetadataOnlyIntentSchema.extend({
        policy: z.enum(['standard', 'pincerManeuver']),
        unit: HordeSessionUnitSchema,
    }),
    DESTROY_FORTIFIED_MARKER: MetadataOnlyIntentSchema.extend({
        objectiveId: IdSchema,
        reason: z.string().min(1),
    }),
    DESTROY_OBJECTIVE: MetadataOnlyIntentSchema.extend({
        objectiveId: IdSchema,
        reason: z.string().min(1),
    }),
    DISCARD_ACTIVE_MISERY: MetadataOnlyIntentSchema.extend({
        cardIds: z.array(IdSchema),
    }),
    DRAW_MISERY: MetadataOnlyIntentSchema.extend({
        cards: z.array(
            z.object({
                cardId: IdSchema,
                effectIds: z.array(IdSchema),
                revealId: IdSchema,
            })
        ),
        randomResultIds: z.array(IdSchema),
    }),
    DRAW_SECONDARY: MetadataOnlyIntentSchema.extend({
        cardIds: z.array(IdSchema).min(1),
        randomResultIds: z.array(IdSchema),
    }),
} satisfies Record<string, z.ZodType>;
